import { createContext } from "react"
import openSocket from "socket.io-client"
import { decodeJwt } from "jose"

const parametrosIguais = (aParams, bParams) =>
  aParams.length === bParams.length && aParams.every((param, index) => param === bParams[index])

class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager
    this.rawSocket = socketManager.currentSocket
    this.callbacks = []
    this.joins = []

    this.rawSocket.on("connect", () => {
      if (!this.rawSocket.recovered) {
        const refreshJoinsOnReady = () => {
          for (const j of this.joins) {
            console.debug("refreshing join", j)
            this.rawSocket.emit(`join${j.event}`, ...j.params)
          }
          this.rawSocket.off("ready", refreshJoinsOnReady)
        }
        for (const j of this.callbacks) {
          this.rawSocket.off(j.event, j.callback)
          this.rawSocket.on(j.event, j.callback)
        }

        this.rawSocket.on("ready", refreshJoinsOnReady)
      }
    })
  }

  on(event, callback) {
    if (event === "ready" || event === "connect") {
      return this.socketManager.onReady(callback)
    }
    this.callbacks.push({ event, callback })
    return this.rawSocket.on(event, callback)
  }

  off(event, callback) {
    if (!callback) {
      this.callbacks = this.callbacks.filter((c) => c.event !== event)
      return this.rawSocket.off(event)
    }

    const i = this.callbacks.findIndex((c) => c.event === event && c.callback === callback)
    if (i >= 0) {
      this.callbacks.splice(i, 1)
    }
    return this.rawSocket.off(event, callback)
  }

  emit(event, ...params) {
    if (event.startsWith("join")) {
      const cEventoJoin = event.substring(4)
      const lJoinRegistrado = this.joins.some(
        (join) => join.event === cEventoJoin && parametrosIguais(join.params, params),
      )

      if (!lJoinRegistrado) {
        this.joins.push({ event: cEventoJoin, params })
      }
    }

    if (event.startsWith("leave")) {
      const cEventoLeave = event.substring(5)
      this.joins = this.joins.filter(
        (join) => !(join.event === cEventoLeave && parametrosIguais(join.params, params)),
      )
    }
    return this.rawSocket.emit(event, ...params)
  }

  disconnect() {
    for (const j of this.joins) {
      this.rawSocket.emit(`leave${j.event}`, ...j.params)
    }
    this.joins = []
    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback)
    }
    this.callbacks = []
  }
}

class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,

  getSocket: function (companyId) {
    let userId = null
    if (localStorage.getItem("userId")) {
      userId = localStorage.getItem("userId")
    }

    if (!companyId && !this.currentSocket) {
      return new DummySocket()
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`
    }

    if (companyId !== this.currentCompanyId || userId !== this.currentUserId) {
      if (this.currentSocket) {
        console.warn("closing old socket - company or user changed")
        this.currentSocket.removeAllListeners()
        this.currentSocket.disconnect()
        this.currentSocket = null
      }

      let tokenString = localStorage.getItem("token")
      if (!tokenString) {
        return new DummySocket()
      }

      let token = JSON.parse(tokenString)
      try {
        if (!token || typeof token !== "string") {
          console.warn("Invalid token, returning dummy socket")
          return new DummySocket()
        }
        const { exp } = decodeJwt(token) ?? {}
        if (Date.now() >= exp * 1000) {
          console.warn("Expired token, reload after refresh")
          setTimeout(() => {
            window.location.reload()
          }, 1000)
          return new DummySocket()
        }
      } catch (err) {
        console.warn("Error decoding token, returning dummy socket", err)
        return new DummySocket()
      }

      this.currentCompanyId = companyId
      this.currentUserId = userId

      if (!token) {
        return new DummySocket()
      }

      this.currentSocket = openSocket(process.env.REACT_APP_BACKEND_URL, {
        transports: ["websocket"],
        pingTimeout: 18000,
        pingInterval: 18000,
        query: { token },
      })

      if (this.currentSocket) {
        this.currentSocket.on("disconnect", (reason) => {
          console.warn(`socket disconnected because: ${reason}`)
          if (reason.startsWith("io ")) {
            console.warn("tryng to reconnect", this.currentSocket)

            const { exp: exp2 } = decodeJwt(token)
            if (Date.now() - 180 >= exp2 * 1000) {
              console.warn("Expired token, reloading app")
              window.location.reload()
              return
            }

            this.currentSocket.connect()
          }
        })

        this.currentSocket.on("connect", (...params) => {
          console.warn("socket connected", params)
        })

        this.currentSocket.onAny((event, ...args) => {
          console.debug("Event: ", { socket: this.currentSocket, event, args })
        })

        // Ao voltar à aba: se o socket caiu, força reconnect (joins são refeitos no ManagedSocket)
        if (typeof document !== "undefined" && !this._visibilityBound) {
          this._visibilityBound = true
          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState !== "visible" || !this.currentSocket) return
            if (!this.currentSocket.connected) {
              console.warn("socket reconnect on visibility")
              this.currentSocket.connect()
            }
          })
        }

        this.onReady(() => {
          this.socketReady = true
        })
      }
    }

    return new ManagedSocket(this)
  },

  onReady: function (callbackReady) {
    if (this.socketReady) {
      callbackReady()
      return
    }

    this.currentSocket.once("ready", () => {
      callbackReady()
    })
  },
}

const SocketContext = createContext()

export { SocketContext, SocketManager }
